import { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { Box, Grid2, Stack, Typography, useTheme } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { EGInput } from "../../../../MainComponents/Input/EGInput";
import { EGBasicSelect } from "../../../../MainComponents/Select/EGBasicSelect";
import EGDatePicker from "../../../../MainComponents/DatePicker/EGDatePicker";
import { InvestigationButton } from "../components/InvestigationButton";
import { returnIsValid, useConstants } from "../Main/useConstants";
import { useSelector } from "react-redux";
import { SectionedSelect } from "../../../../MainComponents/Select/SectionedSelect";
import { catalogByPart, setReportTagByColor } from "../../../../../apis";
import { useParams } from "react-router-dom";

// Funcion revisar el formulario y retornar si es valido y cuales son los errores
export const validateForm = (form) => {
  const { kind, name, indicated, status } = form;
  const errors = {};
  if (kind.trim() === "") {
    errors.kind = true;
  }
  if (name.trim() === "") {
    errors.name = true;
  }
  if (indicated === null) {
    errors.indicated = true;
  }
  if (typeof status === "string" && status.trim() === "") {
    errors.status = true;
  }
  if (typeof status === "boolean") {
    errors.status = true;
  }
  return [
    Object.entries(errors).length === 0 ? true : false,
    errors
  ];
}

// Agregar un involucrado
export const AddInvolved = ({
  handleSaveInvolveds,
  involvedsInInvestigation,
  setIsAdding,
}) => {

  const [t] = useTranslation("investigation");
  const theme = useTheme();
  const { id: reportId } = useParams();

  const jaguarState = useSelector((state) => state.jaguar);
  const { email, username } = useSelector((state) => state.auth);
  const { enums, selects: { typeUserLabels }, involveds: { involvedTypeOpts, statusOpts, reinsidenceOpts } } = useConstants();

  const isManagerOpts = [
    { label: t("involved.yes"), value: "yes" },
    { label: t("involved.no"), value: "no" },
  ];

  const [kind, setKind] = useState("");
  const [name, setName] = useState("");
  const [additional, setAdditional] = useState("");
  const [area, setArea] = useState("");
  const [relationship, setRelationship] = useState("");
  const [indicated, setIndicated] = useState(null);
  const [status, setStatus] = useState(statusOpts[0].value);
  const [details, setDetails] = useState("");
  const [gender, setGender] = useState("");
  const [reinsidence, setReinsidence] = useState("");
  const [triedToAdd, setTriedToAdd] = useState(false);
  const [errors, setErrors] = useState({});
  const [areaOptions, setAreaOptions] = useState([]);
  const [workstationOptions, setWorkstationOptions] = useState([]);
  const [genderOptions, setGenderOptions] = useState([]);
  const [isManager, setIsManager] = useState("");

  // Campos para offender
  const [companyRelationship, setCompanyRelationship] = useState("");
  const [meliEntryDate, setMeliEntryDate] = useState(null);
  const [rfcTaxId, setRfcTaxId] = useState("");
  const [companyRelationshipOptions, setCompanyRelationshipOptions] = useState([]);

  // Campos adicionales para accomplice - Comentado temporalmente
  // const [temporarySeparation, setTemporarySeparation] = useState("");
  // const [separationStartDate, setSeparationStartDate] = useState(null);
  // const [separationEndDate, setSeparationEndDate] = useState(null);
  // const [vertical, setVertical] = useState("");
  // const [position, setPosition] = useState("");
  // const [email, setEmail] = useState("");
  // const [separationOptions, setSeparationOptions] = useState([]);

  const indicatedOpts = useMemo(() => {
    const { COMPLAINANT, /* INVOLVEDS_ACCOMPLICE, */ INVOLVEDS_OFFENDER, INVOLVEDS_VICTIM, INVOLVEDS_WITNESS, SYSTEM } = enums.typeUser;
    const usersWanted = [COMPLAINANT, INVOLVEDS_VICTIM, /* INVOLVEDS_ACCOMPLICE, */ INVOLVEDS_OFFENDER, INVOLVEDS_WITNESS, SYSTEM];

    let newIndicatedOpts = [];

    usersWanted.forEach((value) => {
      if (value === COMPLAINANT) {
        newIndicatedOpts.push({
          key: value,
          options: [{
            value: COMPLAINANT,
            label: t("type_users.COMPLAINANT"),
          }],
        });
        return;
      }

      const fromArray = _.get(jaguarState, enums.typeUserRoutes[value])?.formatted || [];

      // Si es SYSTEM, agregar el usuario actual al inicio de la lista
      if (value === SYSTEM && email) {
        const currentUser = {
          value: email,
          label: username || email,
        };
        newIndicatedOpts.push({
          label: typeUserLabels[value],
          key: value,
          options: [currentUser, ...fromArray],
        });
        return;
      }

      newIndicatedOpts.push({
        label: typeUserLabels[value],
        key: value,
        options: fromArray,
      });
    });

    return newIndicatedOpts;
  }, [jaguarState, email, username]);

  // Cargar opciones del catálogo areaOfExpertise
  useEffect(() => {
    const loadAreaOptions = async () => {
      try {
        const response = await catalogByPart({
          catalogue: "areaOfExpertise",
          is_own: true,
          path: "/"
        });
        if (response?.data) {
          const options = response.data.map((item) => ({
            label: item.label,
            value: item.path_locales,
          }));
          setAreaOptions(options);
        }
      } catch (error) {
        console.error("Error loading area of expertise catalogue:", error);
        setAreaOptions([]);
      }
    };

    loadAreaOptions();
  }, []);

  // Cargar opciones del catálogo workstation
  useEffect(() => {
    const loadWorkstationOptions = async () => {
      try {
        const response = await catalogByPart({
          catalogue: "workstation",
          is_own: true,
          path: "/"
        });
        if (response?.data) {
          const options = response.data.map((item) => ({
            label: item.label,
            value: item.path_locales,
          }));
          setWorkstationOptions(options);
        }
      } catch (error) {
        console.error("Error loading workstation catalogue:", error);
        setWorkstationOptions([]);
      }
    };

    loadWorkstationOptions();
  }, []);

  // Cargar opciones del catálogo genderOfTheInvolvedPerson
  useEffect(() => {
    const loadGenderOptions = async () => {
      try {
        const response = await catalogByPart({
          catalogue: "genderOfTheInvolvedPerson",
          is_own: true,
          path: "/"
        });
        if (response?.data) {
          const options = response.data.map((item) => ({
            label: item.label,
            value: item.path_locales,
          }));
          setGenderOptions(options);
        }
      } catch (error) {
        console.error("Error loading gender catalogue:", error);
        setGenderOptions([]);
      }
    };

    loadGenderOptions();
  }, []);

  // Cargar opciones del catálogo companyRelationshipOffender
  useEffect(() => {
    const loadCompanyRelationshipOptions = async () => {
      try {
        const response = await catalogByPart({
          catalogue: "companyRelationshipOffender",
          is_own: true,
          path: "/"
        });
        if (response?.data) {
          const options = response.data.map((item) => ({
            label: item.label,
            value: item.path_locales,
          }));
          setCompanyRelationshipOptions(options);
        }
      } catch (error) {
        console.error("Error loading company relationship catalogue:", error);
        setCompanyRelationshipOptions([]);
      }
    };

    loadCompanyRelationshipOptions();
  }, []);

  // Cargar opciones del catálogo separation - Comentado temporalmente (para accomplice)
  // useEffect(() => {
  //   const loadSeparationOptions = async () => {
  //     try {
  //       const response = await catalogByPart({
  //         catalogue: "separation",
  //         is_own: true,
  //         path: "/"
  //       });
  //       if (response?.data) {
  //         const options = response.data.map((item) => ({
  //           label: item.label,
  //           value: item.path_locales,
  //         }));
  //         setSeparationOptions(options);
  //       }
  //     } catch (error) {
  //       console.error("Error loading separation catalogue:", error);
  //       setSeparationOptions([]);
  //     }
  //   };

  //   loadSeparationOptions();
  // }, []);

  // Validate every change
  useEffect(() => {
    if (triedToAdd) {
      const [_, errors] = validateForm({ kind, name, indicated, status });
      setErrors(errors);
    }
  }, [kind, name, indicated, status]);

  const handleAddInvolved = async () => {
    const isOffender = kind === "offender";
    // const isAccomplice = kind === "accomplice"; // Comentado temporalmente
    const [isValid, errors] = validateForm({ kind, name, indicated, status });
    setErrors(errors);
    setTriedToAdd(true);
    if (!isValid) {
      enqueueSnackbar({ variant: "error", message: t("missing_required_inputs") });
      return;
    };
    const newInvolveds = _.cloneDeep(involvedsInInvestigation);
    const data = {
      kind,
      rol: kind,
      name,
      additional,
      area,
      relationship,
      indicated,
      gender,
      reinsidence,
      validated: false,
      confirm: status === "confirmed" ? true : false,
      confirm_text: status,
      details,
      isManager,
      // Campos para offender (y accomplice comentado temporalmente)
      ...(isOffender /* || isAccomplice */ ? {
        companyRelationship,
        meliEntryDate: meliEntryDate ? dayjs(meliEntryDate).format("YYYY-MM-DD") : "",
        rfcTaxId,
        // temporarySeparation, // Comentado temporalmente (para accomplice)
        // separationStartDate: separationStartDate ? dayjs(separationStartDate).format("YYYY-MM-DD") : "", // Comentado temporalmente (para accomplice)
        // separationEndDate: separationEndDate ? dayjs(separationEndDate).format("YYYY-MM-DD") : "", // Comentado temporalmente (para accomplice)
        // vertical, // Comentado temporalmente (para accomplice)
        // position, // Comentado temporalmente (para accomplice)
        // email, // Comentado temporalmente (para accomplice)
      } : {}),
    }
    newInvolveds[kind].push(data);
    await handleSaveInvolveds(newInvolveds);

    // Si el involucrado es manager, agregar tag rojo al reporte
    if (isManager === "yes") {
      try {
        await setReportTagByColor({ report_id: reportId, tag: "red" });
        enqueueSnackbar({
          variant: "info",
          message: t("involved.manager_tag_added")
        });
      } catch (error) {
        console.error("Error adding red tag:", error);
      }
    }
  }

  return (
    <Box padding={2}>

      <Box display="flex" alignItems="center" marginBottom={1.5}>
        <Typography
          fontWeight={600}
          fontSize={16}
          color={theme.palette.ethicsBlue.main}
        >
          {t("involved.add_new")}
        </Typography>
        <Box 
          flexGrow={1} 
          height="1px" 
          sx={{ 
            backgroundColor: theme.palette.ethicsBlue.main,
            marginLeft: 1
          }}  
        />
      </Box>

      <Grid2 container spacing={2}>

        {/* Tipo de involucrado */}
        <Grid2 size={{ xs: 12, lg: 6 }}>
          <EGBasicSelect
            label={t("involved.type")}
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            options={involvedTypeOpts}
            required={true}
            isValid={returnIsValid(errors?.kind, triedToAdd)}
            showSuccessValidation={triedToAdd}
            showHelpText={false}
          />
        </Grid2>

        {/* Nombre completo */}
        <Grid2 size={{ xs: 12, lg: 6 }}>
          <EGInput
            label={t("involved.full_name")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required={true}
            isValid={returnIsValid(errors?.name, triedToAdd)}
            showSuccessValidation={triedToAdd}
            showHelpText={false}
          />
        </Grid2>

        {/* Señalado por */}
        <Grid2 size={{ xs: 12, lg: 6 }}>
          <SectionedSelect
            label={t("involved.indicated_by")}
            value={indicated?.id ?? ""}
            onChange={({ value, section }) => setIndicated({ id: value, from: section })}
            sections={indicatedOpts}
            required
            isValid={returnIsValid(errors?.indicated, triedToAdd)}
            showSuccessValidation={true}
            showHelpText={false}
          />
        </Grid2>

        {/* Número de empleado */}
        <Grid2 size={{ xs: 12, lg: 6 }}>
          <EGInput
            label={t("involved.employee_number")}
            value={additional}
            onChange={(e) => setAdditional(e.target.value)}
            type="number"
          />
        </Grid2>

        {/* Área */}
        <Grid2 size={{ xs: 12, lg: 6 }}>
          <EGBasicSelect
            label={t("involved.area")}
            value={area}
            onChange={(e) => setArea(e.target.value)}
            options={areaOptions}
          />
        </Grid2>

        {/* Puesto */}
        <Grid2 size={{ xs: 12, lg: 6 }}>
          <EGBasicSelect
            label={t("involved.position")}
            value={relationship}
            onChange={(e) => setRelationship(e.target.value)}
            options={workstationOptions}
          />
        </Grid2>

        {/* Género */}
        <Grid2 size={{ xs: 12, lg: 6 }}>
          <EGBasicSelect
            label={t("involved.gender")}
            value={gender}
            onChange={(e) => setGender(e.target.value)}
            options={genderOptions}
          />
        </Grid2>

        {/* Reincidencia */}
        <Grid2 size={{ xs: 12, lg: 6 }}>
          <EGBasicSelect
            label={t("involved.reinsidence")}
            value={reinsidence}
            onChange={(e) => setReinsidence(e.target.value)}
            options={reinsidenceOpts}
          />
        </Grid2>

        {/* Estatus */}
        <Grid2 size={{ xs: 12, lg: 6 }}>
          <EGBasicSelect
            label={t("involved.status")}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            options={statusOpts}
            required={true}
            isValid={returnIsValid(errors?.status, triedToAdd)}
            showSuccessValidation={triedToAdd}
            showHelpText={false}
          />
        </Grid2>

        {/* ¿Es Manager? */}
        <Grid2 size={{ xs: 12, lg: 6 }}>
          <EGBasicSelect
            label={t("involved.is_manager")}
            value={isManager}
            onChange={(e) => setIsManager(e.target.value)}
            options={isManagerOpts}
          />
        </Grid2>

        {/* Campos específicos para offender (accomplice comentado temporalmente) */}
        {
          (kind === "offender" /* || kind === "accomplice" */) && (
                  <>
                    <Grid2 size={{ xs: 12, lg: 6 }}>
                      <EGBasicSelect
                        label={t("involved.company_relationship")}
                        value={companyRelationship}
                        onChange={(e) => setCompanyRelationship(e.target.value)}
                        options={companyRelationshipOptions}
                      />
                    </Grid2>

                    <Grid2 size={{ xs: 12, lg: 6 }}>
                      <EGDatePicker
                        label={t("involved.meli_entry_date")}
                        value={meliEntryDate}
                        onChange={(newValue) => setMeliEntryDate(newValue)}
                      />
                    </Grid2>

                    <Grid2 size={{ xs: 12, lg: 6 }}>
                      <EGInput
                        label={t("involved.rfc_tax_id")}
                        value={rfcTaxId}
                        onChange={(e) => setRfcTaxId(e.target.value)}
                      />
                    </Grid2>

                    {/* Campos adicionales para accomplice - Comentado temporalmente */}
                    {/* <Grid2 size={{ xs: 12, lg: 6 }}>
                      <EGBasicSelect
                        label={t("involved.temporary_separation")}
                        value={temporarySeparation}
                        onChange={(e) => setTemporarySeparation(e.target.value)}
                        options={separationOptions}
                      />
                    </Grid2>

                    <Grid2 size={{ xs: 12, lg: 6 }}>
                      <EGDatePicker
                        label={t("involved.separation_start_date")}
                        value={separationStartDate}
                        onChange={(newValue) => setSeparationStartDate(newValue)}
                      />
                    </Grid2>

                    <Grid2 size={{ xs: 12, lg: 6 }}>
                      <EGDatePicker
                        label={t("involved.separation_end_date")}
                        value={separationEndDate}
                        onChange={(newValue) => setSeparationEndDate(newValue)}
                      />
                    </Grid2>

                    <Grid2 size={{ xs: 12, lg: 6 }}>
                      <EGInput
                        label={t("involved.vertical")}
                        value={vertical}
                        onChange={(e) => setVertical(e.target.value)}
                      />
                    </Grid2>

                    <Grid2 size={{ xs: 12, lg: 6 }}>
                      <EGInput
                        label={t("involved.cargo")}
                        value={position}
                        onChange={(e) => setPosition(e.target.value)}
                      />
                    </Grid2>

                    <Grid2 size={{ xs: 12, lg: 6 }}>
                      <EGInput
                        label={t("involved.email")}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        type="email"
                      />
                    </Grid2> */}
                  </>
                )
        }

        {/* Detalles */}
        <Grid2 size={12}>
          <EGInput
            label={t("involved.details")}
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            type="textarea"
            multiline
            rows={5}
          />
        </Grid2>
      </Grid2>

      <Stack direction={{ xs: "column", md: "row" }} justifyContent="flex-end" spacing={1} marginTop={2}>
        <InvestigationButton
          label={t("involved.cancel")}
          icon="close_icon"
          size="medium"
          onClick={() => setIsAdding(false)}
          />
        <InvestigationButton
          label={t("involved.save")}
          variant="success"
          size="medium"
          icon="mark_check_bold"
          onClick={handleAddInvolved}
        />
      </Stack>
    </Box>
  )
}